// Subir el video y pedir la generación de "Recrear video" —
// ADR-2026-10-09-recrear-video-adentro-de-recrear y ADR-2026-10-16-recrear-video-con-nuestra-receta.
//
// Todo lo que toca la red vive acá para que la pantalla quede con estado y dibujo. El
// pedido va a la ruta propia del backend (`/v1/videos/recrear`), que corre la receta del
// laboratorio y entrega en 1080.

import { signReferenceImage, uploadReferenceVideo } from '../../agentAttachments';
import { atlasBackendFetch, pollJobUntilSettled } from '../../../../lib/atlasBackend';
import { GenerateAdError, mapErrorTypeToMessage, type GenerateAdErrorType } from '../../../../lib/generateAdRequest';
import { idsDeVideo } from '../creadorVideo/creadorVideo';
import { fetchModelById, fotoDeModeloParaReferencia } from '../../modelsApi';
import { pollGeneratedByExecutionId } from '../../pollGeneratedByExecutionId';
import {
  MAX_VIDEO_MB,
  duracionDeSalida,
  formatoDelVideo,
  referenciasParaRecrear,
  type ImagenDeRecrear,
  type VideoDeReferencia,
} from './recrearVideo';

/**
 * Lo que dura un video local y su proporción, leyendo la metadata en un `<video>` suelto.
 * `duracion` 0 si no se sabe (un formato que el navegador no lee).
 */
export function medirVideoDeArchivo(file: File): Promise<{ duracion: number; proporcion?: number }> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    const url = URL.createObjectURL(file);
    v.onloadedmetadata = () => {
      const d = v.duration;
      const proporcion = v.videoWidth > 0 && v.videoHeight > 0 ? v.videoWidth / v.videoHeight : undefined;
      URL.revokeObjectURL(url);
      resolve({ duracion: Number.isFinite(d) ? d : 0, proporcion });
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ duracion: 0 });
    };
    v.src = url;
  });
}

/** El chequeo de peso, antes de subir: un error claro en vez de una subida que no termina. */
export function errorDePeso(file: File): string | null {
  return file.size > MAX_VIDEO_MB * 1024 * 1024
    ? `El video pesa más de ${MAX_VIDEO_MB} MB. Probá con uno más corto o más liviano.`
    : null;
}

/**
 * Sube el video del usuario a su carpeta (`<empresa>/agent-videos/`, la misma de los
 * videos que se adjuntan en el chat) y lo firma para dibujarlo.
 */
export async function subirVideoPropio(
  file: File,
  companyId: string,
  medida: { duracion: number; proporcion?: number },
): Promise<VideoDeReferencia> {
  const { storagePath, bucket } = await uploadReferenceVideo(file, companyId);
  const url = await signReferenceImage(storagePath, bucket);
  if (!url) throw new Error('No se pudo cargar el video que subiste.');
  return {
    origen: 'propio',
    url,
    storagePath,
    duracionSeg: medida.duracion,
    proporcion: medida.proporcion,
    nombre: file.name,
  };
}

/** La URL del video propio que viaja al backend: se firma de nuevo (la de la vista pudo vencer). */
async function urlDelVideoPropio(video: VideoDeReferencia): Promise<string> {
  if (!video.storagePath) throw new Error('Falta el video de referencia.');
  const firmada = await signReferenceImage(video.storagePath);
  if (!firmada) throw new Error('No se pudo leer el video de referencia. Subilo de nuevo.');
  return firmada;
}

export interface PedidoDeRecrearVideo {
  companyId: string;
  video: VideoDeReferencia;
  /** La plantilla elegida (su `id` es el del backend), o `null` si el video es propio. */
  plantillaId: string | null;
  imagenes: ImagenDeRecrear[];
  indicaciones: string;
}

/**
 * Cuánto tarda, para la barra de la tanda. Medido en el lab y en el backend (16/10): unos
 * 4 min fijos (análisis, motor en cola, escalado) más ~25 s por segundo de video.
 */
export const duracionEstimadaDeRecrearMs = (segundos: number): number => (240 + 25 * segundos) * 1000;

/** El trabajo puede tardar hasta ~15 min (más si el motor tiene cola): se espera hasta 35. */
const ESPERA_MAXIMA_MS = 35 * 60 * 1000;

/**
 * La tanda lista para `lanzarTanda`: la promesa YA en vuelo y lo que dibujan los recuadros.
 * `POST /v1/videos/recrear` responde 202 con `job_id` en el acto; después se espera el job
 * (`/v1/jobs/:id`) hasta que entrega el `video_id` de la fila del Historial.
 */
export function pedirRecrearVideo(p: PedidoDeRecrearVideo): {
  resultado: Promise<string[]>;
  duracionEstimadaMs: number;
} {
  const segundos = p.plantillaId ? p.video.duracionSeg : duracionDeSalida(p.video.duracionSeg);

  const resultado = (async () => {
    const [urlDelVideo, firmadas] = await Promise.all([
      p.plantillaId ? Promise.resolve(null) : urlDelVideoPropio(p.video),
      firmarSubidas(p.imagenes),
    ]);
    const referencias = referenciasParaRecrear(p.imagenes, firmadas);
    if (referencias.length === 0) throw new Error('No pudimos leer las imágenes. Sumalas de nuevo.');
    const executionId = crypto.randomUUID();
    const cuerpo: Record<string, unknown> = {
      execution_id: executionId,
      ...(p.plantillaId ? { plantilla_id: p.plantillaId } : { video_url: urlDelVideo, duracion_segundos: segundos }),
      referencias,
      ...(p.indicaciones.trim() ? { indicaciones: p.indicaciones.trim().slice(0, 1500) } : {}),
      // El formato del original: es el de la fila del Historial.
      formato: formatoDelVideo(p.video.proporcion),
    };
    // El POST se reintenta con el MISMO execution_id: si el backend ya aceptó el trabajo y se perdió la respuesta, el
    // reintento se engancha a ese mismo trabajo (la ruta deduplica por execution_id) en vez de pagar otro (review
    // Greptile #188). Si aun así no hay job_id pero alguna respuesta se perdió, se espera la fila del Historial.
    let res: Response | null = null;
    let seCortoAlguna = false;
    for (let intento = 1; intento <= 3; intento++) {
      try {
        res = await atlasBackendFetch('/v1/videos/recrear', cuerpo, { signal: AbortSignal.timeout(60_000) });
        if (res.status < 500) break;
      } catch {
        seCortoAlguna = true;
        res = null;
      }
      await new Promise((r) => setTimeout(r, 1500 * intento));
    }
    const json = (res ? await res.json().catch(() => null) : null) as { job_id?: string; error_type?: string; error?: string } | null;
    // Sólo se espera la fila cuando NO hubo una respuesta clara (todo se cortó o terminó en 5xx) y alguna se perdió. Un
    // rechazo claro (402 sin créditos, 404 sin acceso, 400) se muestra al toque: esperar 35 min una fila que nunca va a
    // existir tapaba el motivo real (review Greptile #188).
    const sinRespuestaClara = !res || res.status >= 500;
    if (!json?.job_id && seCortoAlguna && sinRespuestaClara) return esperarLaFila(executionId, p.companyId);
    if (!res) throw new Error('No se pudo conectar. Revisá tu conexión.');
    // Un 429 después de una respuesta perdida puede ser este mismo pedido ya aceptado contando en el tope: se avisa
    // dónde va a aparecer si entró, sin esperar.
    if (res.status === 429 && seCortoAlguna) {
      throw new GenerateAdError('validation', `${json?.error ?? 'Ya tenés 2 videos recreándose.'} Si este pedido llegó a entrar, va a aparecer en el Historial.`, 'quota_exceeded');
    }
    if (!res.ok || !json?.job_id) {
      const tipo = (json?.error_type as GenerateAdErrorType) || 'technical';
      // La ruta apagada (o sólo para dev testers) responde 404.
      if (res.status === 404) throw new GenerateAdError('validation', 'Recrear video todavía no está disponible para tu cuenta.', 'no_disponible');
      throw new GenerateAdError(tipo === ('plan' as GenerateAdErrorType) ? 'validation' : tipo, json?.error || mapErrorTypeToMessage(tipo), json?.error_type ?? null);
    }
    const final = await pollJobUntilSettled(json.job_id, { timeoutMs: ESPERA_MAXIMA_MS, intervalMs: 8000 });
    if (final?.status === 'succeeded') {
      const out = (final.output ?? {}) as { video_id?: string | number };
      return idsDeVideo(out.video_id != null ? String(out.video_id) : null);
    }
    if (final?.status === 'failed') {
      const t = (final.errorType as GenerateAdErrorType) || 'technical';
      throw new GenerateAdError(t, final.userMessage ?? mapErrorTypeToMessage(t), final.errorType);
    }
    // Sigue corriendo: el Historial lo muestra cuando llegue (la fila se busca por execution_id).
    throw new Error('El video está tardando más de lo normal. Va a aparecer en el Historial cuando termine.');
  })();

  return { resultado, duracionEstimadaMs: duracionEstimadaDeRecrearMs(segundos) };
}

/**
 * Una respuesta del POST se perdió y no hay `job_id`: el trabajo pudo haber entrado igual. Se espera su fila del
 * Historial por `execution_id` (la escribe el backend al entregar o al fallar), como hacía el camino viejo.
 */
async function esperarLaFila(executionId: string, companyId: string): Promise<string[]> {
  const filas = await pollGeneratedByExecutionId({ executionId, companyId, expectedCount: 1, timeoutMs: ESPERA_MAXIMA_MS });
  const fila = filas[0];
  if (!fila) throw new Error('No pudimos confirmar el pedido. Si se generó, va a aparecer en el Historial.');
  if (fila.has_error) {
    const t = (fila.error_type as GenerateAdErrorType) || 'technical';
    throw new GenerateAdError(t, mapErrorTypeToMessage(t), fila.error_type);
  }
  return idsDeVideo(fila.id);
}

/**
 * Firma lo que viaja por link: las subidas (por su `storagePath`) y las modelos guardadas, con una firma NUEVA de su
 * foto (`fotoDeModeloParaReferencia`): la de la lista vence a la hora y la pantalla pudo quedar abierta más (review
 * Greptile #188). Los productos del catálogo viajan por id.
 */
async function firmarSubidas(imagenes: ImagenDeRecrear[]): Promise<Record<string, string>> {
  const pares = await Promise.all(
    imagenes.map(async (img) => {
      if (img.origen === 'url' && img.modeloId != null) {
        const m = await fetchModelById(img.modeloId).catch(() => null);
        const url = m ? await fotoDeModeloParaReferencia(m) : null;
        return url ? ([img.id, url] as const) : null;
      }
      if (img.origen !== 'subida' || !img.storagePath) return null;
      const url = await signReferenceImage(img.storagePath);
      return url ? ([img.id, url] as const) : null;
    }),
  );
  return Object.fromEntries(pares.filter((x): x is readonly [string, string] => x !== null));
}
